import React from 'react';
import { CheckRounded, CloseRounded } from '@mui/icons-material';
import { Box, CircularProgress, Typography, styled, useTheme } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { alpha } from '@mui/material/styles';

export type DayStatusIndicatorState =
  | 'noneCompleted'
  | 'someCompleted'
  | 'noActivity'
  | 'completedAll'
  | 'currentDayEmpty';

export interface DayCompletionData {
  completed: number;
  date: string;
  total: number;
}

export interface DayStatusIndicatorProps {
  label: string;
  state: DayStatusIndicatorState;
  underlineLabel?: boolean;
  completionData?: Pick<DayCompletionData, 'completed' | 'total'>;
  size?: number;
  thickness?: number;
}

interface StatusCircleProps {
  sizePx: number;
}

const iconSize = 26;

// Shared sizing so every day-of-week row (checklist, medications) renders uniformly.
export const DAY_STATUS_INDICATOR_SIZE = 38;
export const MOBILE_DAY_STATUS_INDICATOR_SIZE = 28;
export const DAY_STATUS_INDICATOR_THICKNESS = 7;

const IndicatorContainer = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const StatusCircle = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'sizePx',
})<StatusCircleProps>(({ sizePx }) => ({
  width: sizePx,
  height: sizePx,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const ProgressContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.palette.primary.main,
}));

const ProgressTrack = styled(CircularProgress)(({ theme }) => ({
  color: alpha(theme.palette.primary.main, 0.2),
}));

const ProgressValue = styled(CircularProgress)({
  color: 'primary.main',
  position: 'absolute',
  left: 0,
});

const IndicatorLabel = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'underlineLabel',
})<{ underlineLabel?: boolean }>(({ underlineLabel, theme }) => ({
  textDecoration: underlineLabel ? 'underline' : 'none',
  textUnderlineOffset: '4px',
  color: theme.palette.text.primary,
}));

const NoneCompletedCircle = styled(StatusCircle)(({ theme }) => ({
  backgroundColor: theme.palette.grey[500],
}));

const NoActivityCircle = styled(StatusCircle)(({ theme }) => ({
  backgroundColor: theme.palette.grey[300],
}));

const CompletedAllCircle = styled(StatusCircle)(({ theme }) => ({
  backgroundColor: '#73BE5E',
}));

const CurrentDayEmptyCircle = styled(StatusCircle)(({ theme }) => ({
  border: `7px solid ${alpha(theme.palette.primary.main, 0.18)}`,
  boxSizing: 'border-box',
  backgroundColor: theme.palette.common.white,
}));

const WhiteCloseIcon = styled(CloseRounded)(({ theme }) => ({
  color: theme.palette.common.white,
  fontSize: iconSize,
}));

const WhiteCheckIcon = styled(CheckRounded)(({ theme }) => ({
  color: theme.palette.common.white,
  fontSize: iconSize,
}));

export const getCompletionPercentage = (completed: number, total: number) => {
  if (total <= 0) {
    return 0;
  }

  const percentage = (completed / total) * 100;
  return Math.min(100, Math.max(0, percentage));
};

const ProgressIndicator = ({
  label,
  value,
  size,
  thickness,
}: {
  label: string;
  value: number;
  size: number;
  thickness: number;
}) => (
  <ProgressContainer>
    <ProgressTrack
      variant="determinate"
      value={100}
      size={size}
      thickness={thickness}
      aria-hidden
    />
    <ProgressValue
      variant="determinate"
      value={value}
      thickness={thickness}
      size={size}
      aria-label={`${label} completion`}
    />
  </ProgressContainer>
);

export const DayStatusIndicator = ({
  label,
  state,
  underlineLabel = false,
  completionData,
  size: sizeOverride,
  thickness = DAY_STATUS_INDICATOR_THICKNESS,
}: DayStatusIndicatorProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const size =
    sizeOverride ?? (isMobile ? MOBILE_DAY_STATUS_INDICATOR_SIZE : DAY_STATUS_INDICATOR_SIZE);
  const completed = completionData?.completed ?? 0;
  const total = completionData?.total ?? 0;
  const completionPercentage = getCompletionPercentage(completed, total);

  let indicator = null;

  if (state === 'noneCompleted') {
    indicator = (
      <NoneCompletedCircle sizePx={size} role="img" aria-label={`${label} none completed`}>
        <WhiteCloseIcon />
      </NoneCompletedCircle>
    );
  }

  if (state === 'noActivity') {
    indicator = <NoActivityCircle sizePx={size} role="img" aria-label={`${label} no activity`} />;
  }

  if (state === 'completedAll') {
    indicator = (
      <CompletedAllCircle sizePx={size} role="img" aria-label={`${label} completed all`}>
        <WhiteCheckIcon />
      </CompletedAllCircle>
    );
  }

  if (state === 'currentDayEmpty') {
    indicator = (
      <CurrentDayEmptyCircle sizePx={size} role="img" aria-label={`${label} current day empty`} />
    );
  }

  if (state === 'someCompleted') {
    indicator = (
      <ProgressIndicator
        label={label}
        value={completionPercentage}
        size={size}
        thickness={thickness}
      />
    );
  }

  return (
    <IndicatorContainer>
      {indicator}
      <IndicatorLabel variant="body1" underlineLabel={underlineLabel}>
        {label}
      </IndicatorLabel>
    </IndicatorContainer>
  );
};
